import { useForm } from 'react-hook-form';
import { FormProvider, Table, TextField } from '../../../../../components';
import { Container, Dialog, IconButton, Paper, Stack, Typography, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { request, toast } from '../../../../../utils';
import { useLocation } from 'react-router-dom';
import { CustomButtonn } from '../../../../../components';

const TABLE_HEADER = [
  { header: 'Service Name', accessor: 'service_name' },
  { header: 'Code', accessor: 'code' },
  { header: 'Rate', accessor: 'rate' },
  { header: 'GST %', accessor: 'gst_per' },
  { header: 'Status', accessor: 'status', sx: { textTransform: 'uppercase' } }
];

const initialDataObj = { data: [], loading: false };

export const PhysiotherapyServiceMaster = () => {
  const { pathname } = useLocation();
  const lastPathSegment = pathname.split('/').pop();
  const [loading, setLoading] = useState(false);
  const [currentAction, setCurrentAction] = useState();
  const [selectedRecord, setSelectedRecord] = useState();
  const [openQueryDialog, setOpenQueryDialog] = useState(false);
  const [dataObj, setDataObj] = useState(initialDataObj);

  const methods = useForm({
    resolver: yupResolver(
      Yup.object().shape({
        service_name: Yup.string().required('Service name is required'),
        rate: Yup.number().required('Rate is required').min(0)
      })
    ),
    defaultValues: { service_name: '', code: '', rate: 0, gst_per: 0 },
    mode: 'all'
  });
  const { handleSubmit, setValue, reset } = methods;

  useEffect(() => {
    if (selectedRecord) {
      setValue('service_name', selectedRecord.service_name || '');
      setValue('code', selectedRecord.code || '');
      setValue('rate', selectedRecord.rate || 0);
      setValue('gst_per', selectedRecord.gst_per || 0);
    } else {
      reset();
    }
  }, [selectedRecord, setValue, reset]);

  const formSubmit = async (formValues) => {
    setLoading(true);
    const isEdit = selectedRecord && currentAction === 'edit';
    const response = await request(
      isEdit ? '/physiotherapy-service-master-edit' : '/physiotherapy-service-master-create',
      {
        method: isEdit ? 'PUT' : 'POST',
        params: isEdit ? selectedRecord._id : null,
        body: formValues
      }
    );
    if (response?.success) {
      toast(`Successfully ${isEdit ? 'updated' : 'created'} service`, { variant: 'success' });
      reset();
      setCurrentAction();
      setSelectedRecord(null);
    } else {
      toast(`Failed to ${isEdit ? 'update' : 'create'} service`, { variant: 'error' });
    }
    setLoading(false);
  };

  const handleQueryWindow = async () => {
    setOpenQueryDialog(true);
    setDataObj({ loading: true, data: [] });
    const response = await request('/physiotherapy-service-master-list', { method: 'GET' });
    if (response?.success) {
      setDataObj({ loading: false, data: response.data || [] });
    } else {
      toast('Error fetching data', { variant: 'error' });
      setDataObj({ loading: false, data: [] });
    }
  };

  const handleDeleteRecord = async () => {
    if (!selectedRecord) return;
    setLoading(true);
    const response = await request('/physiotherapy-service-master-delete', {
      method: 'DELETE',
      params: selectedRecord._id
    });
    if (response?.success) {
      toast('Successfully deleted service', { variant: 'success' });
      setSelectedRecord(null);
      reset();
    } else {
      toast('Failed to delete service', { variant: 'error' });
    }
    setLoading(false);
  };

  return (
    <>
      <Paper sx={{ p: 0, flex: 1, borderRadius: 0, boxShadow: 'none' }}>
        <Container
          sx={{
            mt: 1,
            border: '0px solid',
            borderColor: 'grey.300',
            borderRadius: 1,
            width: '100%',
            maxWidth: 'none',
            minWidth: '100%',
            boxSizing: 'border-box'
          }}
        >
          <FormProvider methods={methods} onSubmit={handleSubmit(formSubmit)}>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 1 }}
            >
              <Typography color="primary" variant="h3" sx={{ fontWeight: 700 }}>
                Physiotherapy Service Master
              </Typography>
              <Stack direction="row" justifyContent="flex-end" sx={{ mb: 3 }} spacing={1}>
                <CustomButtonn
                  id={`${lastPathSegment}Write`}
                  variant="contained"
                  disabled={['edit', 'new'].includes(currentAction)}
                  onClick={() => { setSelectedRecord(null); setCurrentAction('new'); reset(); }}
                >
                  New
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Update`}
                  variant="contained"
                  disabled={['new', 'edit'].includes(currentAction) || !selectedRecord}
                  onClick={() => setCurrentAction('edit')}
                >
                  Edit
                </CustomButtonn>
                <CustomButtonn
                  id="Submit"
                  type="submit"
                  variant="contained"
                  disabled={!['edit', 'new'].includes(currentAction) || loading}
                >
                  {loading ? 'Saving...' : 'Save'}
                </CustomButtonn>
                <CustomButtonn
                  id="isSuperAdmin"
                  variant="contained"
                  disabled={!selectedRecord || ['new', 'edit'].includes(currentAction)}
                  onClick={handleDeleteRecord}
                >
                  Delete
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Read`}
                  variant="contained"
                  onClick={handleQueryWindow}
                  disabled={['new', 'edit'].includes(currentAction)}
                >
                  Query
                </CustomButtonn>
                <CustomButtonn
                  id="Exit"
                  variant="contained"
                  onClick={() => setCurrentAction('')}
                >
                  Exit
                </CustomButtonn>
              </Stack>
            </Stack>
            <Box
              sx={{
                border: '0px solid gray',
                padding: 2,
                borderRadius: '8px',
                width: '60%',
                boxShadow: '0px 2px 10px #0099ff'
              }}
            >
              <Stack spacing={1}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Service Name</Typography>
                  <TextField
                    name="service_name"
                    sx={{ flex: 0.7 }}
                    disabled={!['new', 'edit'].includes(currentAction)}
                    placeholder="Service Name"
                  />
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Code</Typography>
                  <TextField
                    name="code"
                    sx={{ flex: 0.7 }}
                    disabled={!['new', 'edit'].includes(currentAction)}
                    placeholder="Service Code"
                  />
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Rate</Typography>
                  <TextField
                    name="rate"
                    sx={{ flex: 0.7 }}
                    type="number"
                    inputProps={{ min: '0' }}
                    disabled={!['new', 'edit'].includes(currentAction)}
                    placeholder="Rate"
                  />
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>GST %</Typography>
                  <TextField
                    name="gst_per"
                    sx={{ flex: 0.7 }}
                    type="number"
                    inputProps={{ min: '0', max: '100' }}
                    disabled={!['new', 'edit'].includes(currentAction)}
                    placeholder="GST %"
                  />
                </Stack>
              </Stack>
            </Box>
          </FormProvider>
        </Container>
      </Paper>

      <Dialog
        open={openQueryDialog}
        onClose={() => setOpenQueryDialog(false)}
        PaperProps={{ sx: { width: '70%', p: 2 } }}
        maxWidth="xl"
        scroll="body"
      >
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          borderBottom="1px solid"
          borderColor="grey.300"
          pb={1}
        >
          <Typography variant="h4" fontWeight="bold">
            Physiotherapy Service Master Query
          </Typography>
          <IconButton onClick={() => setOpenQueryDialog(false)}>
            <CloseIcon color="error" fontSize="large" />
          </IconButton>
        </Stack>
        <Table
          sx={{ mt: 0.5 }}
          columns={TABLE_HEADER}
          data={dataObj.data}
          loading={dataObj.loading}
          onRowClick={(row) => {
            setSelectedRecord(row);
            setOpenQueryDialog(false);
          }}
        />
      </Dialog>
    </>
  );
};
